import { useId, useMemo } from "react";

type Props = {
  isCall: boolean;
  strike: number; // in the same unit as spot (ETH per token)
  premium: number; // per option, same unit
  spot: number;
  height?: number;
  labelFor?: (price: number) => string; // e.g. price -> "$12M mcap"
};

/**
 * Profit at expiry per option across a range of prices around the strike. The line turns
 * green where the position is in profit, red where it loses the premium.
 */
export function PayoffChart({ isCall, strike, premium, spot, height = 160, labelFor }: Props) {
  const uid = useId().replace(/:/g, "");
  const W = 520;
  const H = height;
  const pad = 14;
  const { path, zeroY, be, xs, spotX, profitClip } = useMemo(() => {
    const lo = Math.min(spot, strike) * 0.4;
    const hi = Math.max(spot, strike) * 1.9;
    const n = 80;
    const pts: [number, number][] = [];
    for (let i = 0; i <= n; i++) {
      const p = lo + ((hi - lo) * i) / n;
      const v = (isCall ? Math.max(0, p - strike) : Math.max(0, strike - p)) - premium;
      pts.push([p, v]);
    }
    const maxV = Math.max(...pts.map((q) => q[1]), premium);
    const minV = -premium;
    const x = (p: number) => pad + ((p - lo) / (hi - lo)) * (W - pad * 2);
    const y = (v: number) => pad + ((maxV - v) / (maxV - minV || 1)) * (H - pad * 2);
    const d = pts.map(([p, v], i) => `${i ? "L" : "M"}${x(p).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
    const beP = isCall ? strike + premium : strike - premium;
    return { path: d, zeroY: y(0), be: beP, xs: { strike: x(strike), be: x(beP) }, spotX: x(spot), profitClip: y(0) };
  }, [isCall, strike, premium, spot, H]);

  const color = isCall ? "var(--call)" : "var(--put)";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label="Profit at expiry">
      <defs>
        <clipPath id={`${uid}a`}>
          <rect x="0" y="0" width={W} height={profitClip} />
        </clipPath>
        <clipPath id={`${uid}b`}>
          <rect x="0" y={profitClip} width={W} height={H} />
        </clipPath>
      </defs>
      <line x1={pad} x2={W - pad} y1={zeroY} y2={zeroY} stroke="var(--line-2)" />
      <line x1={xs.strike} x2={xs.strike} y1={pad} y2={H - pad} stroke="var(--line-2)" strokeDasharray="3 4" />
      <line x1={spotX} x2={spotX} y1={pad} y2={H - pad} stroke="var(--mute)" strokeOpacity=".5" />
      <path d={path} fill="none" stroke={color} strokeWidth="3" strokeLinejoin="round" clipPath={`url(#${uid}a)`} />
      <path d={path} fill="none" stroke="var(--faint)" strokeWidth="3" strokeLinejoin="round" clipPath={`url(#${uid}b)`} />
      <circle cx={xs.be} cy={zeroY} r="4" fill={color} />
      <text x={spotX + 4} y={H - 2} fontSize="10" fill="var(--mute)" fontFamily="var(--f-mono)">
        now
      </text>
      {labelFor && (
        <text
          x={Math.min(xs.be + 8, W - 130)}
          y={zeroY + 18 > H - 4 ? zeroY - 12 : zeroY + 18}
          fontSize="11"
          fill={color}
          fontFamily="var(--f-mono)"
          stroke="var(--ink)"
          strokeWidth="4"
          paintOrder="stroke"
        >
          breakeven {labelFor(be)}
        </text>
      )}
    </svg>
  );
}
